import { useEffect, useRef, useState } from "react";
import { TradeFlowJson } from "../../../types/TradeFlow";
import EditableField from "../FlowTemplate/EditableField";
import { ArrowUTurnLeft, DocumentTextIcon, SpinnerIcon } from "../../Icons";
import ShipmentBookingForm from "./ShipmentBookingForm";
import QuotesList, { LogisticsRequestQuoteUI } from "./QuotesList";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";
import Tabs from "../../Shared/Tabs";
import FlowBuilder from "../FlowTemplate/FlowBuilder";

const tabs = [{ label: "Shipment Booking" }, { label: "RFQs" }];

export default function EditFlow() {
  const { tradeFlowData, updateTradeFlow } = useTradeFlowData();

  const { trade_flow_json, row_id, id } = tradeFlowData;

  const [steps, setSteps] = useState<TradeFlowJson[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [initialSteps, setInitialSteps] = useState<TradeFlowJson[]>([]);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const editContainerRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    const handler = () => {
      const el = editContainerRef.current;
      if (!el) return;

      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    };

    window.addEventListener("scroll-to-bottom", handler);
    return () => window.removeEventListener("scroll-to-bottom", handler);
  }, []);

  useEffect(() => {
    if (trade_flow_json) {
      setSteps(trade_flow_json);
      setInitialSteps(trade_flow_json);
    }

    return () => {
      setSteps([]);
      setInitialSteps([]);
    };
  }, [tradeFlowData]);

  const handleOnDropStep = (idx: number) => {
    if (dragIndex === null || dragIndex === idx) return;
    const updated = [...steps];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(idx, 0, moved);
    setSteps(updated);
    setDragIndex(null);
  };

  const insertStep = (idx: number) => {
    const newStep: TradeFlowJson = {
      step: { field: "New Step", complted: false },
      Contacts: [],
      tasks: [],
      document_vault: [],
    };
    const updated = [...steps];
    updated.splice(idx + 1, 0, newStep);
    setSteps(updated);
  };

  const handleRemoveStep = (idx: number) => {
    const updated = [...steps];
    updated.splice(idx, 1);
    setSteps(updated);
  };

  const handleSubmit = async () => {
    setIsUpdateLoading(true);
    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: steps,
    });
    setIsUpdateLoading(false);
  };

  const shipmentBookingStep = steps.find(
    (step) => step.step.field === "Shipment Booking",
  ) as TradeFlowJson & { quotes: LogisticsRequestQuoteUI[] };

  return (
    <section
      ref={editContainerRef}
      className={`space-y-5 ${
        shipmentBookingStep?.step_id ? "h-full" : ""
      } overflow-y-auto will-change-transform`}
    >
      <div className="flex items-center justify-between text-gray-800">
        <p className="text-xs">
          Note: Removing a step will also delete its Contacts and Document
          Vault.
        </p>
        <div className="flex items-center gap-5">
          <button
            onClick={handleSubmit}
            className="px-2 py-1 flex items-center gap-1 rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isUpdateLoading}
          >
            {isUpdateLoading ? (
              <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
            ) : (
              <DocumentTextIcon className="size-4" />
            )}
            Save
          </button>

          <button
            onClick={() => {
              setSteps(initialSteps);
            }}
            className="px-2 py-1 flex items-center gap-1 rounded-lg hover:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isUpdateLoading}
          >
            <ArrowUTurnLeft className="size-4" />
            Reset
          </button>
        </div>
      </div>

      <FlowBuilder>
        <FlowBuilder.Track>
          {steps.map((el, idx) => (
            <FlowBuilder.Item key={idx}>
              <FlowBuilder.Step
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleOnDropStep(idx)}
              >
                <FlowBuilder.Badge>{idx + 1}</FlowBuilder.Badge>

                <EditableField
                  {...{ el, idx, steps, setSteps, initialSteps }}
                />
                {el.step.field === "Shipment Booking" && (
                  <div className="h-12 w-[1px] bg-gray-600" />
                )}

                {!["Shipment Pickup", "Shipment Booking"].includes(
                  el.step.field,
                ) && (
                  <FlowBuilder.RemoveBtn
                    onClick={() => handleRemoveStep(idx)}
                  />
                )}
              </FlowBuilder.Step>

              <FlowBuilder.AddBtn onClick={() => insertStep(idx)} />
            </FlowBuilder.Item>
          ))}
        </FlowBuilder.Track>
      </FlowBuilder>

      {shipmentBookingStep?.step_id &&
        shipmentBookingStep.step.field === "Shipment Booking" && (
          <>
            <Tabs.List
              tabs={tabs}
              activeIndex={activeTab}
              onChange={(idx: number) => setActiveTab(idx)}
              className="max-w-xs"
            />

            <Tabs activeIndex={activeTab}>
              <Tabs.Tab>
                <ShipmentBookingForm
                  shipmentBookingStepId={shipmentBookingStep?.step_id || ""}
                />
              </Tabs.Tab>

              <Tabs.Tab>
                <QuotesList quotes={shipmentBookingStep?.quotes || []} />
              </Tabs.Tab>
            </Tabs>
          </>
        )}
    </section>
  );
}
